import React, { useState, useEffect } from "react";
import FuzzyGeoSearch from "../../search/FuzzyGeoSearch";
import { ReusableCombobox } from "../../forms/ReusableCombobox";
import { Button, Typography } from "@murm/ant";
import { fetchData } from "../../../lib/dataCache";
import styles from "../DataCatalog.module.css";
import { PENDO_ID, pendoId } from "../../../lib/pendoIds";

// The API's level names mix case ("states", "Census tracts"); the Figma lists them in title case.
const levelLabel = (level) => level.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const PlacesTab = ({ onSearch }) => {
  const [selectedGeography, setSelectedGeography] = useState(null);
  const [selectedGeographyLevel, setSelectedGeographyLevel] = useState(null);
  const [geographyOptions, setGeographyOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGeographyLevels = async () => {
      try {
        // Static configuration; cached so re-opening the catalogue does not re-request it.
        const body = await fetchData("/api/config/geography-levels");
        const data = body.geography_levels;

        // Filter out 'country' and convert to array of values
        const { country, ...filteredLevels } = data;
        const options = Object.values(filteredLevels);

        setGeographyOptions(options);
      } catch (error) {
        console.error("Error fetching geography levels:", error);
        setGeographyOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGeographyLevels();
  }, []);

  const handleGeoSelect = (geo) => {
    setSelectedGeography(geo);
  };

  const handleGeoLevelSelect = (level) => {
    setSelectedGeographyLevel(level);
  };

  const handleAddPair = () => {
    if (selectedGeography) {
      const pair = selectedGeographyLevel
        ? `${selectedGeographyLevel} in ${selectedGeography.name}`
        : selectedGeography.name;

      const geographyObject = {
        name: selectedGeography.name,
        level: selectedGeographyLevel || null,
        geo_query: selectedGeography.geo_query,
        geography_pair: pair,
      };
      onSearch(geographyObject);
    }
  };

  // Explorer Figma, 2c Places tab: find a location first; the geography level appears once one is
  // chosen; "Add location" adds the pair.
  return (
    <div className={styles.placesForm}>
      <div className={styles.placesField}>
        <FuzzyGeoSearch
          handleSelect={handleGeoSelect}
          placeholder="Search for a location"
          searchButton
        />
      </div>
      {selectedGeography && (
        <div className={`${styles.placesField} ${styles.levelGroup}`}>
          <Typography.Text className={styles.fieldLabel}>
            Geography level (optional)
          </Typography.Text>
          <ReusableCombobox
            {...pendoId(PENDO_ID.CATALOG_PLACE_LEVEL_SELECT)}
            items={geographyOptions}
            onSelect={(item) => handleGeoLevelSelect(item || null)}
            displayValue={(item) => (item ? levelLabel(item) : "")}
            formatOption={levelLabel}
            placeholder="Select an option"
            isLoading={isLoading}
          />
        </div>
      )}
      <Button {...pendoId(PENDO_ID.CATALOG_ADD_LOCATION)} onClick={handleAddPair}>
        Add location
      </Button>
    </div>
  );
};

export { PlacesTab };
